// ═══════════════════════════════════════════════════════════════════════════════
// jobsShared.js — single source of truth for job-related API calls across
// PostJobScreen, PostJobDetailsScreen, BrowseJobsScreen, FindJobsScreen,
// FullTimeJobsScreen, PartTimeJobsScreen, FreelanceJobsScreen,
// JobsOpportunitiesScreen, AIJobMatching, JobDetailsScreen, JobApplicationScreen,
// JobApplicationsScreen, JobAlertsScreen, NotificationsScreen.
//
// This module enforces cross-company isolation rules and guarantees that a job
// posted by a business is visible to every professional on the find-job flow.
// ═══════════════════════════════════════════════════════════════════════════════

import AsyncStorage from '@react-native-async-storage/async-storage';

export const API_BASE = 'https://api.jobeng.ai';
export const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ── Low-level fetch ─────────────────────────────────────────────────────────
export const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token').catch(() => null);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) return { error: true, status: res.status };
    return await res.json();
  } catch (err) {
    return { error: true, message: err.message };
  }
};

// ── Safe id getters (never default to company 1) ────────────────────────────
export const getBusinessId = async () => {
  try { return parseInt(await AsyncStorage.getItem('company_id')) || 0; } catch { return 0; }
};
export const getUserId = async () => {
  try { return parseInt(await AsyncStorage.getItem('user_id')) || 0; } catch { return 0; }
};

// ── Normalizer: unify field names across business/professional views ────────
export const normalizeJob = (j = {}) => {
  if (!j || typeof j !== 'object') return null;
  const skills =
    Array.isArray(j.required_skills) ? j.required_skills :
    Array.isArray(j.skills)          ? j.skills :
    typeof j.required_skills === 'string'
      ? (() => { try { const p = JSON.parse(j.required_skills); return Array.isArray(p) ? p : j.required_skills.split(',').map(s => s.trim()).filter(Boolean); } catch { return j.required_skills.split(',').map(s => s.trim()).filter(Boolean); } })()
      : [];
  const base = {
    id: j.id,
    title: j.title || 'Untitled',
    company_id: parseInt(j.company_id) || 0,
    company: j.company_name || j.company || '',
    company_name: j.company_name || j.company || '',
    company_logo: null, // filled below
    department: j.department || '',
    description: j.description || j.short_description || '',
    requirements: j.requirements || '',
    responsibilities: j.responsibilities || '',
    benefits: j.benefits || '',
    location: j.location || 'Remote',
    job_type: (j.job_type || j.employment_type || 'full-time').toLowerCase(),
    is_remote: !!(j.is_remote || (j.location || '').toLowerCase().includes('remote')),
    is_urgent: !!(j.is_urgent || j.urgency === 'urgent'),
    is_featured: !!j.is_featured,
    is_active: j.is_active !== false && j.status !== 'closed' && j.status !== 'draft',
    salary_min: Number(j.salary_min) || 0,
    salary_max: Number(j.salary_max) || 0,
    salary_currency: j.salary_currency || 'GBP',
    salary_period: j.salary_period || 'annual',
    required_skills: skills,
    experience_min: Number(j.experience_min) || 0,
    experience_max: Number(j.experience_max) || null,
    education_level: j.education_level || '',
    application_deadline: j.application_deadline || null,
    views_count: Number(j.views_count) || 0,
    applications_count: Number(j.applications_count) || 0,
    posted_date: j.posted_date || j.created_at || new Date().toISOString(),
    created_at: j.created_at || j.posted_date || new Date().toISOString(),
    status: (j.status || (j.is_active === false ? 'closed' : 'active')).toLowerCase(),
    // Source tracking: 'jobeng' = posted via JobEng business side,
    //                  'external' = aggregated from hiring sites
    source: j.source || 'jobeng',
    source_name: j.source_name || null,       // e.g. "Adzuna", "Reed", "Indeed"
    external_url: j.external_url || j.redirect_url || null,
    __raw: j,
  };
  base.company_logo = resolveLogo({ ...j, company_name: base.company_name });
  return base;
};

// ── Response extractor — handles {data:[]}, {jobs:[]}, {rows:[]}, or bare [] ─
const pickArray = (r) => {
  if (!r || r.error) return [];
  if (Array.isArray(r)) return r;
  return r.data || r.jobs || r.rows || r.items || r.results || [];
};

// ── Company logo resolver: JobEng company_profiles → Clearbit fallback ──────
// For external jobs (or internal jobs without a logo), derive a Clearbit logo
// from the company name. Clearbit's logo endpoint is free and doesn't require
// a key: https://logo.clearbit.com/{domain}.com
const CLEARBIT = 'https://logo.clearbit.com/';
const companySlug = (name = '') => {
  const s = String(name).toLowerCase().trim().replace(/\s+(ltd|limited|inc|llc|plc|gmbh|co)\.?$/i, '');
  return s.replace(/[^a-z0-9]/g, '');
};
export const resolveLogo = (job = {}) => {
  if (job.company_logo && /^https?:\/\//.test(job.company_logo)) return job.company_logo;
  if (job.logo_url     && /^https?:\/\//.test(job.logo_url))     return job.logo_url;
  const name = job.company_name || job.company || '';
  const slug = companySlug(name);
  return slug ? `${CLEARBIT}${slug}.com` : null;
};

// ── PROFESSIONAL: list all public active jobs (no company_id filter) ────────
// Use this on FindJobsScreen, BrowseJobsScreen, FullTime/Part/Freelance,
// JobsOpportunitiesScreen, AIJobMatching, JobAlertsScreen, dashboard search.
export const fetchPublicJobs = async (filters = {}) => {
  const { job_type, search, limit = 200 } = filters;
  const parts = ['is_active=true', 'sort=created_at', 'order=desc', `limit=${limit}`];
  if (job_type) parts.push(`job_type=${encodeURIComponent(job_type)}`);
  if (search)   parts.push(`q=${encodeURIComponent(search)}`);
  const qs = parts.join('&');

  // Primary: /api/data/jobs (PostgREST-style generic data endpoint)
  // Backend SHOULD LEFT JOIN company_profiles so each row includes
  // company_name and logo_url — see /docs/backend-jobs-join.md
  const r = await apiFetch(`/api/data/jobs?${qs}`);
  let rows = pickArray(r);

  // Fallback: /api/professional/jobs (curated endpoint if generic one fails)
  if (!rows.length) {
    const r2 = await apiFetch(`/api/professional/jobs?${qs}`);
    rows = pickArray(r2);
  }

  // Final client-side guard: strip anything not active, even if server slipped
  return rows
    .map((row) => ({ ...row, source: 'jobeng' }))
    .map(normalizeJob)
    .filter((j) => j && j.is_active)
    .filter((j) => !job_type || j.job_type === job_type.toLowerCase());
};

// ── PROFESSIONAL: list jobs from external hiring sites ──────────────────────
// Calls a server-side proxy at /api/external/jobs (keeps third-party API keys
// off the mobile app). The proxy should aggregate Adzuna + Reed + TheMuse and
// return a single array in the same shape as internal jobs, with source fields.
// See /docs/backend-external-jobs-proxy.md for the Node/Express snippet.
export const fetchExternalJobs = async (filters = {}) => {
  const { search = '', location = '', job_type, limit = 50 } = filters;
  const parts = [`limit=${limit}`];
  if (search)   parts.push(`q=${encodeURIComponent(search)}`);
  if (location) parts.push(`location=${encodeURIComponent(location)}`);
  if (job_type) parts.push(`job_type=${encodeURIComponent(job_type)}`);
  const r = await apiFetch(`/api/external/jobs?${parts.join('&')}`);
  if (r.error) return [];
  return pickArray(r)
    .map((row) => ({
      ...row,
      source: 'external',
      source_name: row.source_name || row.provider || 'External',
      // External jobs use synthetic ids to avoid collision with internal
      id: row.id && String(row.id).startsWith('ext_') ? row.id : `ext_${row.source_name || 'x'}_${row.id || row.redirect_url || Math.random().toString(36).slice(2)}`,
    }))
    .map(normalizeJob)
    .filter(Boolean);
};

// ── UNIFIED: merge JobEng posts + external hiring sites ─────────────────────
// Use this in FindJobsScreen so professionals see one combined list. Returns
// two arrays plus a merged list, so the screen can render section headers or
// badges if it wants to visually separate the sources.
export const fetchAllJobs = async (filters = {}) => {
  const [internal, external] = await Promise.all([
    fetchPublicJobs(filters),
    fetchExternalJobs(filters).catch(() => []),
  ]);
  // De-dup by company+title (external sites sometimes repost internal jobs)
  const seen = new Set(internal.map(j => `${(j.company || '').toLowerCase()}|${(j.title || '').toLowerCase()}`));
  const extFiltered = external.filter(j => !seen.has(`${(j.company || '').toLowerCase()}|${(j.title || '').toLowerCase()}`));
  // Merge: JobEng jobs always float to the top (own companies first)
  const merged = [...internal, ...extFiltered];
  return { merged, internal, external: extFiltered };
};

// ── BUSINESS: list jobs owned by the currently logged-in company ────────────
export const fetchCompanyJobs = async (companyId = null, opts = {}) => {
  const cid = companyId || (await getBusinessId());
  if (!cid) return []; // guard — no company, no data (prevents leak)
  const limit = opts.limit || 100;
  const r = await apiFetch(
    `/api/data/jobs?company_id=${cid}&sort=created_at&order=desc&limit=${limit}`
  );
  return pickArray(r).map(normalizeJob).filter(Boolean);
};

// ── BUSINESS: create or update a job posting ────────────────────────────────
// Enforces the fields that make the job visible to professionals:
// company_id, is_active=true, posted_date, status='active'.
export const saveJobPosting = async (job, { mode = 'create' } = {}) => {
  const cid = job.company_id || (await getBusinessId());
  const uid = await getUserId();
  if (!cid) return { error: true, message: 'No company_id in session' };

  const nowIso = new Date().toISOString();
  const skillsArr = Array.isArray(job.required_skills)
    ? job.required_skills
    : Array.isArray(job.requiredSkills)
    ? job.requiredSkills
    : typeof (job.required_skills || job.requiredSkills) === 'string'
    ? (job.required_skills || job.requiredSkills).split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const payload = {
    company_id: parseInt(cid),
    posted_by: uid || null,
    title: job.title,
    department: job.department || '',
    description: job.description || '',
    requirements: job.requirements || '',
    responsibilities: job.responsibilities || '',
    benefits: job.benefits || '',
    location: job.location || 'Remote',
    job_type: (job.job_type || job.jobType || job.employmentType || 'full-time').toLowerCase(),
    employment_type: job.employment_type || job.employmentType || job.jobType || 'Full-time',
    experience_level: job.experience_level || job.experienceLevel || '',
    experience_min: Number(job.experience_min || job.experienceMin) || 0,
    experience_max: Number(job.experience_max || job.experienceMax) || null,
    education_level: job.education_level || job.educationLevel || '',
    salary_min: Number(job.salary_min || job.salaryMin) || 0,
    salary_max: Number(job.salary_max || job.salaryMax) || 0,
    salary_currency: job.salary_currency || job.salaryCurrency || 'GBP',
    salary_period: job.salary_period || 'annual',
    required_skills: skillsArr,
    is_remote: !!(job.is_remote || job.isRemote),
    is_urgent: !!(job.is_urgent || job.isUrgent),
    is_featured: !!(job.is_featured || job.isFeatured),
    positions_available: Number(job.positions_available || job.positionsAvailable) || 1,
    // ── CRITICAL for professional visibility ──
    is_active: true,
    status: 'active',
    posted_date: nowIso,
    ...(job.application_deadline || job.applicationDeadline
      ? { application_deadline: job.application_deadline || job.applicationDeadline }
      : {}),
  };

  const method = mode === 'update' ? 'PUT' : 'POST';
  const endpoint = mode === 'update' ? `/api/data/jobs/${job.id}` : '/api/data/jobs';
  const r = await apiFetch(endpoint, { method, body: JSON.stringify(payload) });
  if (r.error) return r;

  // Fan-out: create a matching notification so active professionals are pinged
  try {
    await apiFetch('/api/data/notifications', {
      method: 'POST',
      body: JSON.stringify({
        type: 'new_job',
        title: 'New job available',
        message: `${payload.title} — ${payload.location}`,
        data: JSON.stringify({ job_id: r.data?.id || r.id || job.id, company_id: cid }),
        audience: 'professionals',
        created_at: nowIso,
      }),
    });
  } catch {}

  return r;
};

// ── Toggle job active/inactive (business side) ─────────────────────────────
export const setJobActive = async (jobId, isActive) => {
  return apiFetch(`/api/data/jobs/${jobId}`, {
    method: 'PUT',
    body: JSON.stringify({
      is_active: !!isActive,
      status: isActive ? 'active' : 'closed',
      updated_at: new Date().toISOString(),
    }),
  });
};

// ── Fetch a single job by id (JobDetailsScreen, PostJobDetailsScreen) ───────
export const fetchJobById = async (jobId) => {
  if (!jobId) return null;
  const r = await apiFetch(`/api/data/jobs?id=${jobId}&limit=1`);
  const rows = pickArray(r);
  return rows.length ? normalizeJob(rows[0]) : null;
};

// ── PROFESSIONAL: apply to a job — writes to applications + job_applications ─
export const applyToJob = async (payload) => {
  const uid = await getUserId();
  const body = {
    user_id: uid || null,
    source: 'mobile_app',
    applied_date: new Date().toISOString(),
    status: 'submitted',
    ...payload,
    // ensure job_id is numeric if possible
    job_id: payload.job_id || payload.jobId,
  };
  // Server-side handler at /api/recruitment/apply inserts into both:
  //   applications           (professional dashboard reads this)
  //   job_applications       (business recruiter pipeline reads this)
  const r = await apiFetch('/api/recruitment/apply', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return r;
};

// ── PROFESSIONAL: save / unsave a job ───────────────────────────────────────
export const saveJob = async (jobId, userId = null) => {
  const uid = userId || (await getUserId());
  return apiFetch('/api/data/saved_jobs', {
    method: 'POST',
    body: JSON.stringify({
      user_id: uid || null,
      job_id: jobId,
      saved_at: new Date().toISOString(),
    }),
  });
};
export const unsaveJob = async (savedId) =>
  apiFetch(`/api/data/saved_jobs/${savedId}`, { method: 'DELETE' });

// ── Fetch saved / applied lists for a professional ──────────────────────────
export const fetchSavedJobs = async (userId = null) => {
  const uid = userId || (await getUserId());
  const r = await apiFetch(`/api/data/saved_jobs?limit=100${uid ? `&user_id=${uid}` : ''}`);
  return pickArray(r);
};
export const fetchApplications = async (userId = null) => {
  const uid = userId || (await getUserId());
  const r = await apiFetch(
    `/api/data/applications?sort=created_at&order=desc&limit=100${uid ? `&user_id=${uid}` : ''}`
  );
  return pickArray(r);
};

// ── Notifications helpers ──────────────────────────────────────────────────
export const fetchNotifications = async () => {
  const r = await apiFetch('/api/data/notifications?sort=created_at&order=desc&limit=50');
  return pickArray(r);
};

// ── Local cache keys (keep screens consistent) ──────────────────────────────
export const CACHE = {
  publicJobs:   'jobeng_public_jobs_v2',
  companyJobs:  'jobeng_company_jobs_v2',
  savedJobs:    'jobeng_saved_jobs_v2',
  applications: 'jobeng_applications_v2',
  notifications:'jobeng_notifications_v2',
};

// Default export mirrors named exports for convenience
export default {
  API_BASE, API_KEY, apiFetch,
  getBusinessId, getUserId, normalizeJob, resolveLogo,
  fetchPublicJobs, fetchExternalJobs, fetchAllJobs,
  fetchCompanyJobs, fetchJobById,
  saveJobPosting, setJobActive,
  applyToJob, saveJob, unsaveJob,
  fetchSavedJobs, fetchApplications, fetchNotifications,
  CACHE,
};
